import { ICalcBestCPC, ICalcLimitedCPC } from "../../../../types/metrics.types"
import { isEnabledTarget, resolveTargetCpc, round2 } from "../../../../lib/bidCore"
import { ITargetDataForTable } from "../../types/sbTarget.types"
import { ILadderFixedAcos } from "../../../../types/ladderPolicy.types"

// Spec default when no targetAcosSB-SD row (nor "All other") gives a usable target ACOS.
export const DEFAULT_SB_TARGET_ACOS = 30

export interface ITargetAcosRow {
  type: string
  targetAcos?: number
  dynamicTarget?: boolean
  dynamicTargetAcos?: { from: number; to: number; Acos: number }[]
}

export type SbTargetAcosMode = "fixed-ladder" | "dynamic" | "fixed"

export interface IResolvedTargetAcos {
  targetAcos: number
  targetAcosMode: SbTargetAcosMode
}

function findCampaignTargetAcosRow(
  campaignName: string,
  rows: ITargetAcosRow[] | undefined
): ITargetAcosRow | undefined {
  const words = campaignName.split(/\s+/)
  const all = rows ?? []
  return (
    all.find((r) => words.some((word) => r.type.toLowerCase().includes(word.toLowerCase()))) ??
    all.find((r) => r.type == "All other")
  )
}

function resolveTargetAcosMode(
  row: ITargetAcosRow | undefined,
  ladderFixed?: ILadderFixedAcos
): SbTargetAcosMode {
  if (ladderFixed?.active === true) return "fixed-ladder"
  return row?.dynamicTarget === true ? "dynamic" : "fixed"
}

/**
 * Campaign target ACOS: word-in-type match, then "All other", then DEFAULT_SB_TARGET_ACOS.
 * Organic ladder v3 hold (ladderFixed.active): the FIXED settings value (or the global override),
 * never the dynamic CVR range.
 */
export function resolveCampaignTargetAcosWithMode(
  campaignName: string,
  rows: ITargetAcosRow[] | undefined,
  cvr: number,
  ladderFixed?: ILadderFixedAcos
): IResolvedTargetAcos {
  const row = findCampaignTargetAcosRow(campaignName, rows)
  const targetAcosMode = resolveTargetAcosMode(row, ladderFixed)
  const override = ladderFixed?.fixedTargetAcos
  const acos =
    targetAcosMode === "dynamic"
      ? (row?.dynamicTargetAcos ?? []).find((r) => r.from <= cvr && cvr <= r.to)?.Acos
      : targetAcosMode === "fixed-ladder" && override && override > 0
        ? override
        : row?.targetAcos
  return { targetAcos: acos || DEFAULT_SB_TARGET_ACOS, targetAcosMode }
}

export function resolveCampaignTargetAcos(
  campaignName: string,
  rows: ITargetAcosRow[] | undefined,
  cvr: number,
  ladderFixed?: ILadderFixedAcos
): number {
  return resolveCampaignTargetAcosWithMode(campaignName, rows, cvr, ladderFixed).targetAcos
}

export interface ICampaignAov {
  campaignName: string
  value: number
}

export interface ITargetMetricsSettings {
  defaultBid: number
  minTargetCPC: number
  maxTargetCPC: number
  limitedCPCConditions: ICalcLimitedCPC["conditions"] | null
  aovValue: number
}

export interface ITargetMetricsCalculators {
  calcBestCPC: (params: ICalcBestCPC) => number
}

export function findCampaignAov(
  aovs: ICampaignAov[] | undefined,
  campaignName: string
): ICampaignAov | undefined {
  if (!aovs) {
    return undefined
  }

  return (
    aovs.find((aov) => campaignName.toLowerCase().includes(aov.campaignName.toLowerCase())) ||
    aovs.find((aov) => aov.campaignName.toLowerCase() === "all")
  )
}

export function applyTargetMetrics(
  targets: ITargetDataForTable[],
  settings: ITargetMetricsSettings,
  calculators: ITargetMetricsCalculators
): ITargetDataForTable[] {
  return targets.map((target) =>
    isEnabledTarget(target.targetStatus)
      ? applyMetricsToTarget(target, settings, calculators)
      : excludeTarget(target)
  )
}

function excludeTarget(target: ITargetDataForTable): ITargetDataForTable {
  return {
    ...target,
    excluded: true,
    hold: false,
    targetCPC: null,
    bestCPC: null,
    limitedCPC: null,
    finalBid: null,
  }
}

function applyMetricsToTarget(
  target: ITargetDataForTable,
  settings: ITargetMetricsSettings,
  calculators: ITargetMetricsCalculators
): ITargetDataForTable {
  const { targetCPC, hold } = resolveTargetCPC(target, settings)

  const bestCPC = calculators.calcBestCPC({
    targetCVRCPC: target.targetCVRCPC,
    targetCPC,
    bestCVRCPC: target.bestCVRCPC,
  })

  // SB 30%-ACOS invariant: the bid to push is the target CPC itself (held targets keep
  // their current bid). No bid-change limits and no early clamp: the raise cap and the
  // Amazon bounds apply to the FINAL bid only (computeFinalBid).
  return { ...target, targetCPC, bestCPC, hold, limitedCPC: targetCPC }
}

function resolveTargetCPC(
  target: ITargetDataForTable,
  settings: ITargetMetricsSettings
): { targetCPC: number; hold: boolean } {
  const resolved = resolveTargetCpc({
    cpc: target.cpc,
    targetAcos: target.targetAcos,
    actualAcos: target.acos,
    orders: target.orders,
    clicks: target.clicks,
    aov: settings.aovValue,
    currentBid: target.bid || undefined,
  })

  return {
    targetCPC: round2(resolved.cpc ?? settings.defaultBid),
    hold: resolved.rule === "hold",
  }
}
